import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import api from '../../services/api';
import { demoStore, isDemoModeActive } from '../../services/demoModeService';

type AxiosBaseQueryArgs = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
};

type AxiosBaseQueryError = {
  status?: number;
  data?: any;
  message?: string;
};

export const axiosBaseQuery = (): BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  AxiosBaseQueryError
> => {
  return async ({ url, method = 'GET', params, data, headers }) => {
    if (isDemoModeActive() && url.startsWith('/v1/chat-bots')) {
      const bot = { id: 1, name: 'Asistente Clínica Demo', description: 'Bot sintético para demostraciones', bot_type: 'clinic', bot_model: 'demo', system_prompt: null, welcome_message: 'Hola, ¿en qué puedo ayudarte?', fallback_message: null, channels: ['instagram'], is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), pricing_type: 'premium', schedule_mode: 'always', schedule: null, enabled_mcps: [] };
      if (url.endsWith('/channels/status')) return { data: { instagram: true, whatsapp: false, email: false } };
      if (url.endsWith('/active-by-channel')) return { data: { instagram: bot, whatsapp: null, email: null } };
      if (url.endsWith('/pricing-plans')) return { data: [{ key: 'premium', name: 'Demo', description: 'Acceso temporal de demostración' }] };
      if (url.endsWith('/usage')) return { data: { usage: { 1: 18 } } };
      if (url.endsWith('/general-stats')) return { data: { total_messages_handled: { current: 18, previous: 12, percentage_change: 50 }, appointments_booked: { current: 4, previous: 2, percentage_change: 100 }, unique_users_interacted: { current: 6, previous: 4, percentage_change: 50 } } };
      if (url.endsWith('/stats')) return { data: [{ bot_id: 1, name: bot.name, messages_sent: 7, messages_received: 11, avg_response_time_ms: 1400, last_used_at: new Date().toISOString(), unique_users: 6, appointments_booked: 4, total_tokens: 0, total_cost_usd: 0 }] };
      if (method === 'GET') return { data: { data: [bot] } };
      return { data: url === '/v1/chat-bots' && method === 'POST' ? { ...bot, ...data } : { success: true } };
    }
    if (isDemoModeActive() && url.startsWith('/chat/')) {
      const path = decodeURIComponent(url.split('?')[0]);
      const threadMatch = path.match(/^\/chat\/chat-sessions\/([^/]+)\/tags(?:\/(\d+))?$/);
      if (path === '/chat/tags' && method === 'GET') return { data: demoStore.tags() };
      if (path === '/chat/tags' && method === 'POST') {
        let tag: any;
        demoStore.mutate(state => { tag = { id: demoStore.nextId(state), name: data.name, color: data.color ?? null, folder_id: data.folder_id ?? null }; state.tags.push(tag); });
        return { data: { tag } };
      }
      const tagMatch = path.match(/^\/chat\/tags\/(\d+)$/);
      if (tagMatch) {
        const id = Number(tagMatch[1]);
        demoStore.mutate(state => {
          if (method === 'DELETE') state.tags = state.tags.filter(tag => tag.id !== id);
          else Object.assign(state.tags.find(tag => tag.id === id) || {}, data);
        });
        return { data: { success: true } };
      }
      if (path === '/chat/tag-folders' && method === 'POST') {
        let folder: any;
        demoStore.mutate(state => { folder = { id: demoStore.nextId(state), name: data.name, parent_id: data.parent_id ?? null }; state.folders.push(folder); });
        return { data: { folder } };
      }
      const folderMatch = path.match(/^\/chat\/tag-folders\/(\d+)$/);
      if (folderMatch) {
        const id = Number(folderMatch[1]);
        demoStore.mutate(state => {
          if (method === 'DELETE') state.folders = state.folders.filter(folder => folder.id !== id);
          else Object.assign(state.folders.find(folder => folder.id === id) || {}, data);
        });
        return { data: { success: true } };
      }
      if (threadMatch) {
        const threadId = threadMatch[1];
        const tagId = threadMatch[2] ? Number(threadMatch[2]) : Number(data?.tag_id);
        if (method === 'GET') return { data: { tags: demoStore.threadTags(threadId) } };
        demoStore.mutate(state => {
          const ids = (state.threadTags[threadId] ||= []);
          if (method === 'DELETE') state.threadTags[threadId] = ids.filter(id => id !== tagId);
          else if (tagId && !ids.includes(tagId)) ids.push(tagId);
        });
        return { data: { success: true } };
      }
      if (path === '/chat/thread-tags' && method === 'GET') {
        const queryIds = new URLSearchParams(url.split('?')[1] || '').getAll('thread_ids[]');
        const paramIds = Array.isArray(params?.['thread_ids[]']) ? params!['thread_ids[]'] : [];
        const ids = [...queryIds, ...paramIds].map(String);
        return { data: { mapping: Object.fromEntries(ids.map((id: string) => [id, demoStore.threadTags(id)])) } };
      }
    }
    try {
      const response = await api.request({
        url,
        method,
        params,
        data,
        headers,
      });
      return { data: response.data };
    } catch (err: any) {
      const status: number | undefined = err?.response?.status;
      const payload = {
        status,
        data: err?.response?.data,
        message: err?.message || 'Request failed',
      } as AxiosBaseQueryError;
      return { error: payload };
    }
  };
};
